<template>
  <div class="jim-roi-container roi-page">

    <!-- HEADER -->
    <div class="page-header-row">
      <div class="page-header-left"></div>

      <div class="title-block">
        <h1 class="page-heading">JIM CAPITAL CAPACITY ANALYSIS</h1>
        <div class="page-subheading">
          {{ projectName }} — {{ locationName }}
        </div>
      </div>

      <div class="page-header-right"></div>
    </div>


    <div class="main-content">

      <!-- ========================================================= -->
      <!-- LEFT CONTROLS                                              -->
      <!-- ========================================================= -->
      <div class="left-side-section no-print">

        <div class="panel control-panel">
          <div class="panel-title">Project</div>

          <div class="control">
            <label>Project Name</label>
            <input v-model="projectName" type="text" />
          </div>

          <div class="control">
            <label>Existing JIMs</label>
            <input v-model.number="existingJims" type="number" min="0" />
          </div>

          <div class="control">
            <label>JIMs Added</label>
            <input v-model.number="newJims" type="number" min="1" />
          </div>

          <div class="control">
            <label>Investment Cost</label>
            <input v-model.number="investmentCost" type="number" min="0" step="1000" />
          </div>
        </div>


        <div class="panel control-panel">
          <div class="panel-title">Capacity</div>

          <div class="control">
            <label>New JIM Capacity / Day</label>
            <input v-model.number="newJimCapacityPerDay" type="number" min="0" step="100" />
          </div>

          <div class="control">
            <label>Production Days / Year</label>
            <input v-model.number="productionDaysYear" type="number" min="1" />
          </div>

          <div class="control">
            <label>Current Backlog (ft)</label>
            <input v-model.number="currentBacklogFeet" type="number" min="0" step="1000" />
          </div>

          <div class="control">
            <label>Target Lead / Backlog Days</label>
            <input v-model.number="targetLeadDays" type="number" min="0" step="0.5" />
          </div>
        </div>


        <div class="panel control-panel">
          <div class="panel-title">Knoxville YTD</div>

          <div class="control">
            <label>2026 YTD Footage</label>
            <input v-model.number="ytdFeet" type="number" min="0" />
          </div>

          <div class="control">
            <label>2025 Comparable Footage</label>
            <input v-model.number="priorYtdFeet" type="number" min="0" />
          </div>

          <div class="control">
            <label>Business Days YTD</label>
            <input v-model.number="businessDaysYtd" type="number" min="1" />
          </div>

          <div class="control">
            <label>2026 YTD Revenue</label>
            <input v-model.number="ytdRevenue" type="number" min="0" step="1000" />
          </div>
        </div>


        <div class="panel control-panel">
          <div class="panel-title">Financial Assumptions</div>

          <div class="control">
            <label>Gross Profit %</label>
            <input v-model.number="gpPct" type="number" min="0" max="100" step="0.1" />
          </div>

          <div class="control">
            <label>ROI Margin Basis %</label>
            <input
              v-model.number="roiMarginPct"
              type="number"
              min="0"
              max="100"
              step="0.1"
            />
          </div>

          <div class="small-muted">
            Default is Knoxville operating margin. Replace with true incremental
            contribution margin when available.
          </div>

          <div class="control">
            <label>Added Annual Support Cost</label>
            <input
              v-model.number="annualSupportCost"
              type="number"
              min="0"
              step="1000"
            />
          </div>

          <div class="small-muted">
            Optional: BB overtime, added maintenance, support labor, etc.
          </div>
        </div>


        <button class="plan-it-btn" @click="printReport">
          Print PDF
        </button>

        <button class="plan-it-btn secondary-btn" @click="resetDefaults">
          Restore Knoxville Defaults
        </button>

      </div>


      <!-- ========================================================= -->
      <!-- RIGHT SIDE / REPORT                                       -->
      <!-- ========================================================= -->
      <div class="right-side-section">

        <!-- PRINT META -->
        <div class="print-meta">
          <span>{{ locationName }}</span>
          <span>{{ projectName }}</span>
          <span>Prepared {{ formattedDate }}</span>
        </div>


        <!-- SUMMARY BAR -->
        <div class="summary-bar">

          <div class="summary-item">
            <div class="summary-label">Investment</div>
            <div class="summary-value">
              {{ formatMoney0(investmentCost) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Added Capacity</div>
            <div class="summary-value">
              {{ formatFeetCompact(annualAddedCapacity) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Current Backlog</div>
            <div class="summary-value">
              {{ formatDays(currentBacklogDays) }}
            </div>
          </div>

          <div class="summary-item highlight-summary">
            <div class="summary-label">3-Year Hurdle</div>
            <div class="summary-value">
              {{ formatPct3(selectedThreeYearHurdle?.capacityPct) }}
            </div>
          </div>

        </div>


        <!-- EXECUTIVE CASE -->
        <div class="panel report-panel print-section executive-panel">

          <div class="section-title">
            Investment Case
          </div>

          <p>
            Knoxville shipped
            <strong>{{ formatFeet(ytdFeet) }}</strong>
            through August 2026 compared with
            <strong>{{ formatFeet(priorYtdFeet) }}</strong>
            during the comparable 2025 period, an increase of
            <strong>{{ formatFeet(yoyGrowthFeet) }}</strong>
            or
            <strong>{{ formatPct1(yoyGrowthPct) }}</strong>.
          </p>

          <p>
            Knoxville currently operates
            <strong>{{ existingJims }} JIM machines</strong>
            and carries approximately
            <strong>{{ formatFeet(currentBacklogFeet) }}</strong>
            of backlog. Based on the current YTD shipment rate, that backlog
            represents approximately
            <strong>{{ formatDays(currentBacklogDays) }}</strong>
            of shipments.
          </p>

          <p>
            {{ projectName }} would add approximately
            <strong>{{ formatFeet(annualAddedCapacity) }}</strong>
            of annual weekday production capacity. One year of the proposed
            machine's base capacity is approximately
            <strong>{{ formatPct1(jimCapacityVsActualGrowth) }}</strong>
            of the actual year-over-year footage increase Knoxville has already
            experienced through August.
          </p>

          <p class="conclusion-line">
            At the selected ROI margin basis of
            <strong>{{ roiMarginPct.toFixed(1) }}%</strong>,
            only
            <strong>{{ formatFeet(selectedThreeYearHurdle?.requiredFeet) }}</strong>
            of annual incremental volume —
            approximately
            <strong>{{ formatPct3(selectedThreeYearHurdle?.capacityPct) }}</strong>
            of the proposed JIM's annual capacity —
            is required to achieve a three-year simple payback.
          </p>

        </div>


        <!-- CURRENT OPERATING POSITION -->
        <div class="two-column-grid">

          <div class="panel report-panel print-section">
            <div class="section-title">Current Operating Position</div>

            <table class="detail-table">
              <tbody>
                <tr>
                  <td>Existing JIMs</td>
                  <td>{{ existingJims }}</td>
                </tr>

                <tr>
                  <td>2026 YTD Footage</td>
                  <td>{{ formatFeet(ytdFeet) }}</td>
                </tr>

                <tr>
                  <td>2025 Comparable Footage</td>
                  <td>{{ formatFeet(priorYtdFeet) }}</td>
                </tr>

                <tr class="emphasis-row">
                  <td>YTD Growth</td>
                  <td>
                    {{ formatFeet(yoyGrowthFeet) }}
                    / {{ formatPct1(yoyGrowthPct) }}
                  </td>
                </tr>

                <tr>
                  <td>Average Shipped / Business Day</td>
                  <td>{{ formatFeet(dailyShipmentFeet) }}</td>
                </tr>

                <tr>
                  <td>Current Backlog</td>
                  <td>{{ formatFeet(currentBacklogFeet) }}</td>
                </tr>

                <tr class="emphasis-row">
                  <td>Backlog Equivalent</td>
                  <td>{{ formatDays(currentBacklogDays) }}</td>
                </tr>
              </tbody>
            </table>
          </div>


          <div class="panel report-panel print-section">
            <div class="section-title">Proposed Capacity</div>

            <table class="detail-table">
              <tbody>
                <tr>
                  <td>JIMs Added</td>
                  <td>{{ newJims }}</td>
                </tr>

                <tr>
                  <td>Fleet Increase</td>
                  <td>{{ formatPct1(fleetIncreasePct) }}</td>
                </tr>

                <tr>
                  <td>Capacity / Day</td>
                  <td>{{ formatFeet(totalAddedDailyCapacity) }}</td>
                </tr>

                <tr class="emphasis-row">
                  <td>Annual Added Capacity</td>
                  <td>{{ formatFeet(annualAddedCapacity) }}</td>
                </tr>

                <tr>
                  <td>Actual YTD YoY Growth</td>
                  <td>{{ formatFeet(yoyGrowthFeet) }}</td>
                </tr>

                <tr>
                  <td>JIM Capacity vs YTD Growth</td>
                  <td>{{ formatPct1(jimCapacityVsActualGrowth) }}</td>
                </tr>

                <tr>
                  <td>Simple Annualized Growth Pace</td>
                  <td>{{ formatFeet(annualizedGrowthFeet) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>


        <!-- BACKLOG / LEAD TIME -->
        <div class="panel report-panel print-section">

          <div class="section-title">
            Backlog & Lead-Time Impact
          </div>

          <div class="metric-grid">

            <div class="metric-card">
              <div class="metric-label">Current Backlog</div>
              <div class="metric-value">
                {{ formatDays(currentBacklogDays) }}
              </div>
              <div class="metric-detail">
                {{ formatFeet(currentBacklogFeet) }}
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Target</div>
              <div class="metric-value">
                {{ formatDays(targetLeadDays) }}
              </div>
              <div class="metric-detail">
                {{ formatFeet(targetBacklogFeet) }}
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Required Reduction</div>
              <div class="metric-value">
                {{ formatFeetCompact(backlogReductionFeet) }}
              </div>
              <div class="metric-detail">
                {{ formatDays(currentBacklogDays - targetLeadDays) }}
                reduction
              </div>
            </div>

            <div class="metric-card">
              <div class="metric-label">Estimated Burn-Down</div>
              <div class="metric-value">
                {{ formatDays(daysToTarget) }}
              </div>
              <div class="metric-detail">
                at {{ formatFeet(totalAddedDailyCapacity) }}/day
              </div>
            </div>

          </div>


          <div class="timing-note">
            At the current average revenue per foot, reducing the production
            queue from its current level to the selected target would accelerate
            approximately
            <strong>{{ formatMoney0(backlogRevenueAcceleration) }}</strong>
            of shipment timing. This is a one-time timing benefit and is
            <strong>not included</strong> in the recurring ROI calculations below.
          </div>

        </div>


        <!-- FINANCIAL CAPACITY -->
        <div class="two-column-grid">

          <div class="panel report-panel print-section">
            <div class="section-title">Knoxville Economics</div>

            <table class="detail-table">
              <tbody>
                <tr>
                  <td>YTD Revenue</td>
                  <td>{{ formatMoney0(ytdRevenue) }}</td>
                </tr>

                <tr>
                  <td>YTD Footage</td>
                  <td>{{ formatFeet(ytdFeet) }}</td>
                </tr>

                <tr class="emphasis-row">
                  <td>Average Revenue / Foot</td>
                  <td>{{ formatMoney2(revenuePerFoot) }}</td>
                </tr>

                <tr>
                  <td>Gross Profit %</td>
                  <td>{{ gpPct.toFixed(1) }}%</td>
                </tr>

                <tr>
                  <td>ROI Margin Basis</td>
                  <td>{{ roiMarginPct.toFixed(1) }}%</td>
                </tr>

                <tr>
                  <td>ROI Contribution / Foot</td>
                  <td>{{ formatMoney2(roiContributionPerFoot) }}</td>
                </tr>
              </tbody>
            </table>
          </div>


          <div class="panel report-panel print-section">
            <div class="section-title">Value of Full Added Capacity</div>

            <table class="detail-table">
              <tbody>
                <tr>
                  <td>Annual Added Footage</td>
                  <td>{{ formatFeet(annualAddedCapacity) }}</td>
                </tr>

                <tr class="emphasis-row">
                  <td>Revenue Capacity</td>
                  <td>{{ formatMoney0(fullCapacityRevenue) }}</td>
                </tr>

                <tr>
                  <td>Gross Profit @ {{ gpPct.toFixed(1) }}%</td>
                  <td>{{ formatMoney0(fullCapacityGP) }}</td>
                </tr>

                <tr>
                  <td>Contribution @ {{ roiMarginPct.toFixed(1) }}%</td>
                  <td>{{ formatMoney0(fullCapacityContributionBeforeSupport) }}</td>
                </tr>

                <tr v-if="annualSupportCost > 0">
                  <td>Less Added Annual Support Cost</td>
                  <td>({{ formatMoney0(annualSupportCost) }})</td>
                </tr>

                <tr class="emphasis-row">
                  <td>Net Annual Contribution</td>
                  <td>{{ formatMoney0(fullCapacityNetContribution) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>


        <!-- PAYBACK HURDLES -->
        <div class="panel report-panel print-section">

          <div class="section-title">
            Incremental Sales Required to Justify Investment
          </div>

          <div class="section-subtitle">
            This table does not assume all JIM 9 capacity becomes new sales.
            It shows the minimum incremental annual volume required to achieve
            each payback target.
          </div>

          <div class="table-scroll">
            <table class="roi-table">
              <thead>
                <tr>
                  <th>Payback Target</th>
                  <th>Annual Contribution Required</th>
                  <th>Incremental Revenue Required</th>
                  <th>Incremental Feet Required</th>
                  <th>% of New JIM Capacity</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in paybackRows"
                  :key="row.years"
                  :class="{ 'focus-row': row.years === 3 }"
                >
                  <td>{{ row.years }} Year<span v-if="row.years !== 1">s</span></td>
                  <td>{{ formatMoney0(row.requiredContribution) }}</td>
                  <td>{{ formatMoney0(row.requiredRevenue) }}</td>
                  <td>{{ formatFeet(row.requiredFeet) }}</td>
                  <td>{{ formatPct3(row.capacityPct) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>


        <!-- UTILIZATION SCENARIOS -->
        <div class="panel report-panel print-section">

          <div class="section-title">
            Incremental Sales Capture Scenarios
          </div>

          <div class="section-subtitle">
            Illustrates the return if only a portion of JIM 9 capacity represents
            business JPL would otherwise lose, delay, or be unable to accept.
          </div>

          <div class="table-scroll">
            <table class="roi-table">
              <thead>
                <tr>
                  <th>Incremental Utilization</th>
                  <th>Incremental Feet</th>
                  <th>Incremental Revenue</th>
                  <th>Net Annual Contribution</th>
                  <th>Simple Payback</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="row in scenarioRows"
                  :key="row.utilization"
                  :class="{ 'focus-row': row.utilization === 0.15 }"
                >
                  <td>{{ formatPct3(row.utilization) }}</td>
                  <td>{{ formatFeet(row.feet) }}</td>
                  <td>{{ formatMoney0(row.revenue) }}</td>
                  <td>{{ formatMoney0(row.netContribution) }}</td>
                  <td>{{ formatPayback(row.paybackYears) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>


        <!-- ASSUMPTIONS -->
        <div class="panel report-panel print-section assumptions-panel">

          <div class="section-title">
            Assumptions Used
          </div>

          <div class="assumption-grid">

            <div>
              <span>Investment:</span>
              <strong>{{ formatMoney0(investmentCost) }}</strong>
            </div>

            <div>
              <span>New JIM output:</span>
              <strong>{{ formatFeet(newJimCapacityPerDay) }}/day</strong>
            </div>

            <div>
              <span>Production days:</span>
              <strong>{{ productionDaysYear }}/year</strong>
            </div>

            <div>
              <span>Target backlog:</span>
              <strong>{{ targetLeadDays }} days</strong>
            </div>

            <div>
              <span>ROI margin basis:</span>
              <strong>{{ roiMarginPct.toFixed(1) }}%</strong>
            </div>

            <div>
              <span>Added support cost:</span>
              <strong>{{ formatMoney0(annualSupportCost) }}/year</strong>
            </div>

          </div>

        </div>


        <!-- DISCLOSURE -->
        <div class="report-disclosure">
          <strong>Methodology:</strong>
          Backlog days are calculated using average YTD shipped footage per
          business day. Backlog burn-down assumes demand remains at the current
          daily rate and the proposed JIM's output is additive to current
          production and can be supported by upstream BB capacity. Annual machine
          capacity reflects the base weekday schedule and excludes additional
          Saturday or Sunday production. Investment returns are capacity-based
          estimates; added capacity does not itself guarantee incremental sales.
          Payback calculations represent the incremental sales volume required
          for the investment to achieve the indicated return. The one-time
          shipment timing benefit from backlog reduction is excluded from payback.
        </div>

      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: "JimCapitalROI",

  data() {
    return {
      locationName: "Knoxville (K)",
      projectName: "JIM 9",

      // Existing / proposed equipment
      existingJims: 8,
      newJims: 1,

      // Investment
      investmentCost: 500000,

      // Capacity
      newJimCapacityPerDay: 16000,
      productionDaysYear: 250,

      // Backlog
      currentBacklogFeet: 2004700,
      targetLeadDays: 8,

      // Knoxville operating results through 8/31/2026
      ytdFeet: 26343535,
      priorYtdFeet: 22321594,
      businessDaysYtd: 166,
      ytdRevenue: 38627000,

      // Financial assumptions
      gpPct: 30,
      roiMarginPct: 20,

      // Add BB overtime, extra maintenance, utilities, etc. later if desired
      annualSupportCost: 0,

      generatedAt: new Date()
    };
  },


  computed: {

    yoyGrowthFeet() {
      return this.ytdFeet - this.priorYtdFeet;
    },

    yoyGrowthPct() {
      if (!this.priorYtdFeet) return 0;
      return this.yoyGrowthFeet / this.priorYtdFeet;
    },

    dailyShipmentFeet() {
      if (!this.businessDaysYtd) return 0;
      return this.ytdFeet / this.businessDaysYtd;
    },

    currentBacklogDays() {
      if (!this.dailyShipmentFeet) return 0;
      return this.currentBacklogFeet / this.dailyShipmentFeet;
    },

    totalAddedDailyCapacity() {
      return this.newJimCapacityPerDay * this.newJims;
    },

    annualAddedCapacity() {
      return this.totalAddedDailyCapacity * this.productionDaysYear;
    },

    fleetIncreasePct() {
      if (!this.existingJims) return 0;
      return this.newJims / this.existingJims;
    },

    annualizedGrowthFeet() {
      if (!this.businessDaysYtd) return 0;

      return (
        (this.yoyGrowthFeet / this.businessDaysYtd) *
        this.productionDaysYear
      );
    },

    jimCapacityVsActualGrowth() {
      if (!this.yoyGrowthFeet) return 0;
      return this.annualAddedCapacity / this.yoyGrowthFeet;
    },

    revenuePerFoot() {
      if (!this.ytdFeet) return 0;
      return this.ytdRevenue / this.ytdFeet;
    },

    roiMarginDecimal() {
      return this.roiMarginPct / 100;
    },

    gpDecimal() {
      return this.gpPct / 100;
    },

    roiContributionPerFoot() {
      return this.revenuePerFoot * this.roiMarginDecimal;
    },

    fullCapacityRevenue() {
      return this.annualAddedCapacity * this.revenuePerFoot;
    },

    fullCapacityGP() {
      return this.fullCapacityRevenue * this.gpDecimal;
    },

    fullCapacityContributionBeforeSupport() {
      return this.fullCapacityRevenue * this.roiMarginDecimal;
    },

    fullCapacityNetContribution() {
      return (
        this.fullCapacityContributionBeforeSupport -
        this.annualSupportCost
      );
    },


    // --------------------------------------------------------------
    // BACKLOG / LEAD TIME
    // --------------------------------------------------------------
    targetBacklogFeet() {
      return this.dailyShipmentFeet * this.targetLeadDays;
    },

    backlogReductionFeet() {
      return Math.max(
        0,
        this.currentBacklogFeet - this.targetBacklogFeet
      );
    },

    daysToTarget() {
      if (!this.totalAddedDailyCapacity) return 0;

      return (
        this.backlogReductionFeet /
        this.totalAddedDailyCapacity
      );
    },

    backlogRevenueAcceleration() {
      return this.backlogReductionFeet * this.revenuePerFoot;
    },


    // --------------------------------------------------------------
    // PAYBACK HURDLES
    // --------------------------------------------------------------
    paybackRows() {
      const yearsList = [1, 2, 3, 5];

      return yearsList.map(years => {

        /*
          Annual contribution needed to achieve target payback.

          Fixed annual support costs are added because the machine must
          generate enough contribution to cover those costs AND provide
          the required capital recovery.
        */
        const requiredContribution =
          (this.investmentCost / years) +
          this.annualSupportCost;

        const requiredRevenue =
          this.roiMarginDecimal > 0
            ? requiredContribution / this.roiMarginDecimal
            : 0;

        const requiredFeet =
          this.revenuePerFoot > 0
            ? requiredRevenue / this.revenuePerFoot
            : 0;

        const capacityPct =
          this.annualAddedCapacity > 0
            ? requiredFeet / this.annualAddedCapacity
            : 0;

        return {
          years,
          requiredContribution,
          requiredRevenue,
          requiredFeet,
          capacityPct
        };
      });
    },

    selectedThreeYearHurdle() {
      return this.paybackRows.find(r => r.years === 3) || null;
    },


    // --------------------------------------------------------------
    // SALES CAPTURE SCENARIOS
    // --------------------------------------------------------------
    scenarioRows() {
      const scenarios = [0.15, 0.25, 0.50, 1.00];

      return scenarios.map(utilization => {

        const feet =
          this.annualAddedCapacity * utilization;

        const revenue =
          feet * this.revenuePerFoot;

        const contributionBeforeSupport =
          revenue * this.roiMarginDecimal;

        const netContribution =
          contributionBeforeSupport -
          this.annualSupportCost;

        const paybackYears =
          netContribution > 0
            ? this.investmentCost / netContribution
            : null;

        return {
          utilization,
          feet,
          revenue,
          contributionBeforeSupport,
          netContribution,
          paybackYears
        };
      });
    },


    formattedDate() {
      return this.generatedAt.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    }

  },


  methods: {

    resetDefaults() {
      this.locationName = "Knoxville (K)";
      this.projectName = "JIM 9";

      this.existingJims = 8;
      this.newJims = 1;

      this.investmentCost = 500000;

      this.newJimCapacityPerDay = 16000;
      this.productionDaysYear = 250;

      this.currentBacklogFeet = 2004700;
      this.targetLeadDays = 8;

      this.ytdFeet = 26343535;
      this.priorYtdFeet = 22321594;
      this.businessDaysYtd = 166;
      this.ytdRevenue = 38627000;

      this.gpPct = 30;
      this.roiMarginPct = 20;
      this.annualSupportCost = 0;

      this.generatedAt = new Date();
    },


    printReport() {
        const params = new URLSearchParams();

        params.set("locationName", this.locationName);
        params.set("projectName", this.projectName);

        params.set("existingJims", String(this.existingJims));
        params.set("newJims", String(this.newJims));

        params.set("investmentCost", String(this.investmentCost));

        params.set("newJimCapacityPerDay", String(this.newJimCapacityPerDay));
        params.set("productionDaysYear", String(this.productionDaysYear));

        params.set("currentBacklogFeet", String(this.currentBacklogFeet));
        params.set("targetLeadDays", String(this.targetLeadDays));

        params.set("ytdFeet", String(this.ytdFeet));
        params.set("priorYtdFeet", String(this.priorYtdFeet));
        params.set("businessDaysYtd", String(this.businessDaysYtd));
        params.set("ytdRevenue", String(this.ytdRevenue));

        params.set("gpPct", String(this.gpPct));
        params.set("roiMarginPct", String(this.roiMarginPct));
        params.set("annualSupportCost", String(this.annualSupportCost));

        const url = `/jim-capital-roi/print?${params.toString()}`;

        console.log("🖨️ [JIM ROI] opening print URL:", url);

        window.open(url, "_blank");
    },


    formatMoney0(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      });
    },


    formatMoney2(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },


    formatFeet(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return (
        n.toLocaleString("en-US", {
          maximumFractionDigits: 0
        }) + " ft"
      );
    },


    formatFeetCompact(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      if (Math.abs(n) >= 1000000) {
        return (n / 1000000).toFixed(2) + "M ft";
      }

      if (Math.abs(n) >= 1000) {
        return (n / 1000).toFixed(0) + "K ft";
      }

      return n.toFixed(0) + " ft";
    },


    formatPct1(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return (n * 100).toFixed(1) + "%";
    },


    formatPct3(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return (n * 100).toFixed(1) + "%";
    },


    formatDays(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "—";

      return n.toFixed(1) + " days";
    },


    formatPayback(value) {
      const n = Number(value);

      if (!Number.isFinite(n)) return "N/A";

      if (n < 1) {
        return (n * 12).toFixed(1) + " months";
      }

      return n.toFixed(2) + " years";
    }

  }
};
</script>


<style scoped>

/* ================================================================ */
/* PAGE BASE                                                        */
/* ================================================================ */

.roi-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

.jim-roi-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}


/* ================================================================ */
/* HEADER                                                           */
/* ================================================================ */

.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 12px;
}

.title-block {
  text-align: center;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

.page-subheading {
  font-size: 18px;
  margin-top: 2px;
  color: #fff;
}


/* ================================================================ */
/* MAIN LAYOUT                                                      */
/* ================================================================ */

.main-content {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 14px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.right-side-section {
  width: 100%;
  min-width: 0;
}


/* ================================================================ */
/* PANELS                                                           */
/* ================================================================ */

.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
  box-sizing: border-box;
}

.control-panel {
  width: 100%;
}

.panel-title {
  font-weight: 800;
  font-size: 15px;
  color: #fff;
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid #444;
}


/* ================================================================ */
/* CONTROLS                                                         */
/* ================================================================ */

.control {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  width: 100%;
  margin-bottom: 9px;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input {
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.control input:focus {
  outline: none;
  border-color: #888;
}

.small-muted {
  font-size: 11px;
  line-height: 1.25;
  opacity: 0.82;
  color: #fff;
  margin-top: -3px;
  margin-bottom: 9px;
}


/* ================================================================ */
/* BUTTONS                                                          */
/* ================================================================ */

.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 190px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.secondary-btn {
  background-color: #d9dde2;
}


/* ================================================================ */
/* PRINT META                                                       */
/* ================================================================ */

.print-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin: 0 2px 7px;
  opacity: 0.85;
}


/* ================================================================ */
/* SUMMARY BAR                                                      */
/* ================================================================ */

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 16px;
  opacity: 0.90;
  line-height: 1.1;
}

.summary-value {
  font-size: 21px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 4px;
}

.highlight-summary {
  border-left: 1px solid #aaa;
}


/* ================================================================ */
/* REPORT PANELS                                                    */
/* ================================================================ */

.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
  margin-bottom: 12px;
}

.section-title {
  color: #fff;
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 6px;
}

.section-subtitle {
  color: #eee;
  font-size: 12px;
  margin-bottom: 9px;
  line-height: 1.3;
}

.executive-panel p {
  margin: 7px 0;
  line-height: 1.35;
  font-size: 14px;
}

.conclusion-line {
  border-top: 1px solid #999;
  padding-top: 9px;
  margin-top: 10px !important;
}

.two-column-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}


/* ================================================================ */
/* DETAIL TABLE                                                     */
/* ================================================================ */

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table td {
  border-bottom: 1px solid #626b73;
  padding: 5px 7px;
  font-size: 13px;
}

.detail-table td:last-child {
  text-align: right;
  font-weight: 700;
  color: #fff;
}

.detail-table tr:last-child td {
  border-bottom: none;
}

.emphasis-row td {
  background-color: rgba(11, 1, 1, 0.40);
  font-weight: 800;
}


/* ================================================================ */
/* BACKLOG METRICS                                                  */
/* ================================================================ */

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 9px;
}

.metric-card {
  background-color: rgba(11, 1, 1, 0.55);
  border: 1px solid #777;
  border-radius: 8px;
  padding: 9px;
  text-align: center;
}

.metric-label {
  font-size: 12px;
  opacity: 0.9;
}

.metric-value {
  color: #fff;
  font-size: 20px;
  font-weight: 800;
  margin-top: 3px;
}

.metric-detail {
  font-size: 11px;
  margin-top: 3px;
  opacity: 0.9;
}

.timing-note {
  font-size: 12px;
  line-height: 1.35;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #777;
}


/* ================================================================ */
/* ROI TABLES                                                       */
/* ================================================================ */

.table-scroll {
  overflow-x: auto;
}

.roi-table {
  width: 100%;
  border-collapse: collapse;
}

.roi-table th,
.roi-table td {
  border: 1px solid #444;
  padding: 6px 8px;
  font-size: 12px;
  white-space: nowrap;
}

.roi-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: 800;
  text-align: right;
}

.roi-table th:first-child {
  text-align: left;
}

.roi-table td {
  text-align: right;
}

.roi-table td:first-child {
  text-align: left;
  font-weight: 700;
}

.roi-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

.focus-row td {
  background-color: rgba(11, 1, 1, 0.55);
  color: #fff;
  font-weight: 800;
}


/* ================================================================ */
/* ASSUMPTIONS                                                      */
/* ================================================================ */

.assumption-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px 14px;
  font-size: 12px;
}

.assumption-grid div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #666;
  padding-bottom: 3px;
}

.assumption-grid strong {
  color: #fff;
}

.report-disclosure {
  font-size: 10px;
  line-height: 1.3;
  opacity: 0.8;
  padding: 3px 5px 15px;
}


/* ================================================================ */
/* RESPONSIVE                                                       */
/* ================================================================ */

@media (max-width: 1100px) {

  .main-content {
    grid-template-columns: 220px 1fr;
  }

  .metric-grid {
    grid-template-columns: 1fr 1fr;
  }

  .two-column-grid {
    grid-template-columns: 1fr;
  }
}


/* ================================================================ */
/* PRINT                                                            */
/* ================================================================ */

@page {
  size: Letter landscape;
  margin: 0.35in;
}

@media print {

  .no-print {
    display: none !important;
  }

  .roi-page,
  .jim-roi-container {
    background: #fff !important;
    color: #000 !important;
    min-height: auto !important;
    padding: 0 !important;
  }

  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  .right-side-section {
    width: 100% !important;
  }

  .page-header-row {
    margin-bottom: 5px !important;
  }

  .page-heading {
    color: #000 !important;
    font-size: 25px !important;
  }

  .page-subheading {
    color: #000 !important;
    font-size: 14px !important;
  }

  .print-meta {
    color: #000 !important;
    font-size: 9px !important;
    margin-bottom: 5px !important;
  }

  .summary-bar,
  .report-panel,
  .metric-card {
    background: #fff !important;
    color: #000 !important;
    border-color: #555 !important;
    box-shadow: none !important;
  }

  .summary-bar {
    padding: 6px 8px !important;
    margin-bottom: 6px !important;
  }

  .summary-label,
  .summary-value,
  .section-title,
  .section-subtitle,
  .detail-table td,
  .detail-table td:last-child,
  .metric-label,
  .metric-value,
  .metric-detail,
  .assumption-grid strong {
    color: #000 !important;
  }

  .summary-label {
    font-size: 11px !important;
  }

  .summary-value {
    font-size: 16px !important;
  }

  .section-title {
    font-size: 14px !important;
    margin-bottom: 3px !important;
  }

  .section-subtitle {
    font-size: 9px !important;
    margin-bottom: 5px !important;
  }

  .report-panel {
    padding: 6px 8px !important;
    margin-bottom: 6px !important;
  }

  .executive-panel p {
    font-size: 10px !important;
    line-height: 1.2 !important;
    margin: 3px 0 !important;
  }

  .detail-table td {
    color: #000 !important;
    font-size: 9px !important;
    padding: 3px 5px !important;
    border-color: #aaa !important;
  }

  .emphasis-row td,
  .focus-row td {
    background: #ececec !important;
    color: #000 !important;
  }

  .metric-grid {
    grid-template-columns: repeat(4, 1fr) !important;
    gap: 5px !important;
  }

  .metric-card {
    padding: 5px !important;
  }

  .metric-label,
  .metric-detail {
    font-size: 8px !important;
  }

  .metric-value {
    font-size: 14px !important;
  }

  .timing-note {
    font-size: 8.5px !important;
    color: #000 !important;
    margin-top: 5px !important;
    padding-top: 4px !important;
  }

  .roi-table th,
  .roi-table td {
    color: #000 !important;
    border-color: #777 !important;
    font-size: 8.5px !important;
    padding: 3px 5px !important;
  }

  .roi-table th {
    background: #e5e5e5 !important;
  }

  .two-column-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 6px !important;
  }

  .assumption-grid {
    grid-template-columns: repeat(3, 1fr) !important;
    font-size: 8.5px !important;
    gap: 3px 10px !important;
  }

  .report-disclosure {
    color: #000 !important;
    font-size: 7.5px !important;
    padding: 2px 2px 0 !important;
  }

  .print-section {
    break-inside: avoid;
    page-break-inside: avoid;
  }

}
</style>